import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useNotificationsStore } from '../store/notificationsStore';
import { INotification } from '../api/api';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background-color: #1A2E2E;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  border: 1px solid #2C4A4A;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: none;
`;

const TabsContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0 24px 20px;
  border-bottom: 1px solid #2C4A4A;
`;

const Tab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  color: ${props => props.$active ? '#24BA7D' : '#838D90'};
  cursor: pointer;
  font-family: 'Ubuntu', sans-serif;
  font-weight: ${props => props.$active ? '500' : '400'};
  font-size: 14px;
  padding: 8px 16px;
  margin-right: 16px;
  border-bottom: 2px solid ${props => props.$active ? '#24BA7D' : 'transparent'};
  transition: all 0.2s ease;
  
  &:hover {
    color: ${props => props.$active ? '#24BA7D' : '#FFFFFF'};
  }
  
  &:last-child {
    margin-right: 0;
  }
`;

const Title = styled.h2`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 20px;
  color: #FFFFFF;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #838D90;
  cursor: pointer;
  font-size: 24px;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    background-color: rgba(131, 141, 144, 0.1);
    color: #FFFFFF;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
`;

const MainView = styled.div`
  padding: 16px 24px;
`;

const DetailView = styled.div`
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #2C4A4A;
  background: #0A1B1F;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #24BA7D;
  cursor: pointer;
  font-family: 'Ubuntu', sans-serif;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(36, 186, 125, 0.1);
  }
`;

const DetailContent = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const DetailTitle = styled.h3`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 18px;
  color: #FFFFFF;
  margin: 0 0 16px 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const DetailMessage = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-size: 14px;
  color: #B8C5C9;
  line-height: 1.5;
  margin-bottom: 16px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  max-width: 100%;
  
  &:not(:first-of-type) {
    margin-top: 8px;
  }
  
  strong {
    color: #FFFFFF;
    font-weight: 500;
    display: block;
    margin-bottom: 8px;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
`;

const DetailMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: #0A1B1F;
  border-radius: 8px;
  border: 1px solid #2C4A4A;
  max-width: 100%;
  overflow: hidden;
  margin-top: 20px;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: 'Ubuntu', sans-serif;
  font-size: 12px;
  gap: 12px;
`;

const MetaLabel = styled.span`
  color: #838D90;
  flex-shrink: 0;
  min-width: 80px;
`;

const MetaValue = styled.span`
  color: #FFFFFF;
  text-align: right;
  word-break: break-word;
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const LoadingDetail = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  font-family: 'Ubuntu', sans-serif;
  color: #838D90;
  font-size: 14px;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #838D90;
  font-family: 'Ubuntu', sans-serif;
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #FF6B6B;
  font-family: 'Ubuntu', sans-serif;
  text-align: center;
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #838D90;
  font-family: 'Ubuntu', sans-serif;
  text-align: center;
`;

const NotificationsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const NotificationItem = styled.div<{ $isRead: boolean }>`
  padding: 16px 24px;
  border-bottom: 1px solid #2C4A4A;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background-color: ${props => props.$isRead ? 'transparent' : 'rgba(36, 186, 125, 0.05)'};
  
  &:hover {
    background-color: rgba(131, 141, 144, 0.05);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const NotificationTitle = styled.h3`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #FFFFFF;
  margin: 0;
  margin-right: 12px;
  line-height: 1.4;
`;

const NotificationMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const NotificationBadge = styled.span<{ $type: INotification['type'] }>`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.$type) {
      case 'success':
        return 'background-color: rgba(36, 186, 125, 0.2); color: #24BA7D;';
      case 'warning':
        return 'background-color: rgba(255, 193, 7, 0.2); color: #FFC107;';
      case 'error':
        return 'background-color: rgba(255, 107, 107, 0.2); color: #FF6B6B;';
      default:
        return 'background-color: rgba(131, 141, 144, 0.2); color: #838D90;';
    }
  }}
`;

const NotificationTime = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-size: 12px;
  color: #838D90;
`;

const NotificationMessage = styled.p`
  font-family: 'Ubuntu', sans-serif;
  font-size: 13px;
  color: #CCCCCC;
  margin: 0;
  line-height: 1.4;
`;

const ReadIndicator = styled.div<{ $isRead: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.$isRead ? 'transparent' : '#24BA7D'};
  flex-shrink: 0;
`;

interface NotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NotificationsModal: React.FC<NotificationsModalProps> = ({ isOpen, onClose }) => {
  const { 
    notifications, 
    unreadNotifications,
    isLoading, 
    error, 
    activeTab,
    selectedNotification,
    isLoadingDetails,
    markAsRead, 
    clearError,
    setActiveTab,
    selectNotification 
  } = useNotificationsStore();

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        clearError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  const handleNotificationClick = async (notification: INotification) => {
    // Отправляем GET запрос для получения деталей уведомления
    // Внутри getNotificationDetails автоматически отправится POST запрос для пометки как прочитанное
    const { getNotificationDetails } = useNotificationsStore.getState();
    await getNotificationDetails(notification.id);
  };

  const handleBackClick = () => {
    selectNotification(null);
  };

  const getCurrentNotifications = () => {
    return activeTab === 'all' ? notifications : unreadNotifications;
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return 'недавно';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return 'только что';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} мин назад`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ч назад`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} дн назад`;
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalContainer>
        <Header>
          <Title>
            {selectedNotification ? 'Детали уведомления' : 'Уведомления'}
          </Title>
          <CloseButton onClick={onClose}>
            ×
          </CloseButton>
        </Header>
        
        {!selectedNotification && (
          <TabsContainer>
            <Tab 
              $active={activeTab === 'all'} 
              onClick={() => setActiveTab('all')}
            >
              Все уведомления
            </Tab>
            <Tab 
              $active={activeTab === 'unread'} 
              onClick={() => setActiveTab('unread')}
            >
              Непрочитанные
            </Tab>
          </TabsContainer>
        )}
        
        <Content>
          {selectedNotification ? (
            <DetailView>
              <DetailHeader>
                <BackButton onClick={handleBackClick}>
                  ← Назад к списку
                </BackButton>
              </DetailHeader>
              
              {isLoadingDetails ? (
                <LoadingDetail>
                  Загрузка деталей уведомления...
                </LoadingDetail>
              ) : (
                <DetailContent>
                  <DetailTitle>{selectedNotification.title}</DetailTitle>
                  
                  {selectedNotification.message && (
                    <DetailMessage>
                      <strong>Сообщение:</strong>
                      {selectedNotification.message}
                    </DetailMessage>
                  )}
                  
                  {selectedNotification.description && (
                    <DetailMessage>
                      <strong>Описание:</strong>
                      {selectedNotification.description}
                    </DetailMessage>
                  )}
                  
                  {selectedNotification.component && (
                    <DetailMessage>
                      <strong>Компонент:</strong>
                      {selectedNotification.component}
                    </DetailMessage>
                  )}
                  
                  <DetailMeta>
                    <MetaRow>
                      <MetaLabel>ID:</MetaLabel>
                      <MetaValue>{selectedNotification.id}</MetaValue>
                    </MetaRow>
                    
                    <MetaRow>
                      <MetaLabel>Тип:</MetaLabel>
                      <MetaValue>
                        <NotificationBadge $type={selectedNotification.type || 'info'}>
                          {selectedNotification.type || 'info'}
                        </NotificationBadge>
                      </MetaValue>
                    </MetaRow>
                    
                    <MetaRow>
                      <MetaLabel>Время:</MetaLabel>
                      <MetaValue>{formatTime(selectedNotification.date)}</MetaValue>
                    </MetaRow>
                    
                    <MetaRow>
                      <MetaLabel>Дата:</MetaLabel>
                      <MetaValue>
                        {new Date(selectedNotification.date).toLocaleString('ru-RU')}
                      </MetaValue>
                    </MetaRow>
                    
                    <MetaRow>
                      <MetaLabel>Статус:</MetaLabel>
                      <MetaValue>
                        {selectedNotification.is_checked ? 'Прочитано' : 'Непрочитано'}
                      </MetaValue>
                    </MetaRow>
                    
                    {selectedNotification.created_at && (
                      <MetaRow>
                        <MetaLabel>Создано:</MetaLabel>
                        <MetaValue>
                          {new Date(selectedNotification.created_at).toLocaleString('ru-RU')}
                        </MetaValue>
                      </MetaRow>
                    )}
                    
                    {selectedNotification.updated_at && (
                      <MetaRow>
                        <MetaLabel>Обновлено:</MetaLabel>
                        <MetaValue>
                          {new Date(selectedNotification.updated_at).toLocaleString('ru-RU')}
                        </MetaValue>
                      </MetaRow>
                    )}
                  </DetailMeta>
                </DetailContent>
              )}
            </DetailView>
          ) : (
            <MainView>
              {isLoading && (
                <LoadingContainer>
                  Загрузка уведомлений...
                </LoadingContainer>
              )}

              {error && (
                <ErrorContainer>
                  {error}
                </ErrorContainer>
              )}

              {!isLoading && !error && getCurrentNotifications().length === 0 && (
                <EmptyContainer>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔔</div>
                  <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                    {activeTab === 'all' ? 'Нет уведомлений' : 'Нет непрочитанных уведомлений'}
                  </div>
                  <div style={{ fontSize: '14px' }}>
                    {activeTab === 'all' 
                      ? 'Здесь будут отображаться важные сообщения' 
                      : 'Все уведомления прочитаны'
                    }
                  </div>
                </EmptyContainer>
              )}

              {!isLoading && !error && getCurrentNotifications().length > 0 && (
                <NotificationsList>
                  {getCurrentNotifications().map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      $isRead={notification.is_checked}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <NotificationHeader>
                        <NotificationTitle>{notification.title}</NotificationTitle>
                        <NotificationMeta>
                          <NotificationBadge $type={notification.type || 'info'}>
                            {notification.type || 'info'}
                          </NotificationBadge>
                          <NotificationTime>
                            {formatTime(notification.date)}
                          </NotificationTime>
                          <ReadIndicator $isRead={notification.is_checked} />
                        </NotificationMeta>
                      </NotificationHeader>
                      {notification.message && (
                        <NotificationMessage>{notification.message}</NotificationMessage>
                      )}
                    </NotificationItem>
                  ))}
                </NotificationsList>
              )}
            </MainView>
          )}
        </Content>
      </ModalContainer>
    </Overlay>
  );
};

export default NotificationsModal;
